import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { STATUS_LABELS } from "@/components/StatusBadge";

const COLUMNS = [
  { key: "CREATED", label: "Created", color: "border-slate-400" },
  { key: "DESIGN_IN_PROGRESS", label: "Design In Progress", color: "border-blue-500" },
  { key: "DESIGN_APPROVED", label: "Design Approved", color: "border-indigo-500" },
  { key: "IN_PRODUCTION", label: "In Production", color: "border-amber-500" },
  { key: "QUALITY_PENDING", label: "Quality Pending", color: "border-yellow-500" },
  { key: "QUALITY_PASSED", label: "Quality Passed", color: "border-emerald-500" },
  { key: "QUALITY_FAILED", label: "Quality Failed", color: "border-red-500" },
  { key: "REWORK", label: "Rework", color: "border-rose-500" },
  { key: "READY_FOR_DISPATCH", label: "Ready for Dispatch", color: "border-teal-500" },
  { key: "DISPATCHED", label: "Dispatched", color: "border-emerald-700" },
  { key: "ON_HOLD", label: "On Hold", color: "border-gray-400" },
  { key: "REJECTED_SCRAPPED", label: "Rejected / Scrapped", color: "border-red-600" },
  { key: "CANCELLED", label: "Cancelled", color: "border-slate-500" },
];

export default function Kanban() {
  const [cylinders, setCylinders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cylinders?limit=1000")
      .then((r) => setCylinders(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[Kanban] /cylinders failed:", err?.response?.status);
        setCylinders([]);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-6 h-6 animate-spin text-blue-900" />
      </div>
    );
  }

  const grouped = {};
  COLUMNS.forEach((c) => { grouped[c.key] = []; });
  cylinders.forEach((c) => {
    if (grouped[c.cylinder_status]) grouped[c.cylinder_status].push(c);
  });

  return (
    <div data-testid="kanban-page" className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Shop Floor View</div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
          Kanban Board
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Live cylinder flow by production state — drag support coming in Module 4.
        </p>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: "calc(100vh - 220px)" }}>
        {COLUMNS.map((col) => (
          <div
            key={col.key}
            data-testid={`kanban-col-${col.key}`}
            className="flex-shrink-0 w-72 bg-slate-100/60 rounded-md border border-slate-200/60 flex flex-col"
          >
            <div className={`px-3 py-2.5 border-b-2 ${col.color} bg-white rounded-t-md`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-800">
                  {col.label}
                </div>
                <span className="text-[11px] font-mono font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                  {grouped[col.key].length}
                </span>
              </div>
            </div>
            <div className="p-2 space-y-2 overflow-y-auto flex-1">
              {grouped[col.key].length === 0 ? (
                <div className="text-[10px] uppercase tracking-wider text-slate-400 text-center py-4">
                  Empty
                </div>
              ) : (
                grouped[col.key].map((c) => (
                  <Link
                    key={c.id}
                    to={`/cylinders/${c.id}`}
                    data-testid={`kanban-card-${c.cylinder_number}`}
                    className="block bg-white p-3 rounded border border-slate-200 hover:border-blue-400 hover:shadow-sm transition-all"
                  >
                    <div className="font-mono text-[11px] font-semibold text-blue-900 truncate">
                      {c.cylinder_number}
                    </div>
                    <div className="mt-1 text-xs text-slate-900 font-medium truncate">
                      {c.customer_name}
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-500 truncate">
                      {c.plant_name} · {c.po_number}
                    </div>
                    {c.days_late != null && (
                      <div className="mt-2 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded inline-block">
                        {c.days_late}d late
                      </div>
                    )}
                  </Link>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
